/** @jsxImportSource theme-ui */
import { gql } from "@apollo/client";
import { Flex, Title } from "@bottlebooks/gatsby-design-system";
import type { ComponentPropsWithoutRef } from "react";
import type { Theme } from "theme-ui";
import { Avatar } from "theme-ui";
import { FragmentType, graphql, useFragment } from "~/gql";
import Skeleton from "../Skeleton";

const fragment = graphql(`
  fragment UserProfileCardAvatar on SiteUserProfile {
    displayName
    profileImage {
      url
      publicId
      cloudName
    }
  }
`);

export default function UserProfileCardAvatar({
  profile: data,
  variant = "default",
  ...rest
}: {
  profile: FragmentType<typeof fragment>;
  variant?: keyof typeof avatarVariants;
}) {
  const profile = useFragment(fragment, data);
  if (!profile) return null;
  const props = avatarVariants[variant];
  return (
    <Flex
      gap={0}
      {...props.root}
      sx={(theme: Theme) => ({
        ...props.root.sx(theme),
        margin: "auto",
        backgroundColor: "nuanced",
        transition: "transform",
        justifyContent: "center",
        alignItems: "center",
        overflow: "hidden",
      })}
      {...rest}
    >
      <AvatarImage data={data} variant={variant} />
    </Flex>
  );
}

export type UserProfileCardAvatarData = {
  displayName: string | null | undefined;
  profileImageUrl?: string | null;
  profileImage?: {
    url: string | null;
    publicId: string | null;
    cloudName: string | null;
  } | null;
};

function AvatarImage({
  data,
  variant = "default",
}: {
  data: FragmentType<typeof fragment>;
  variant?: keyof typeof avatarVariants;
}) {
  const profile = useFragment(fragment, data);
  const props = avatarVariants[variant];
  const url = profile.profileImage?.url;
  if (!url) {
    return (
      <AvatarPlaceholder
        data={data}
        {...props.avatarPlaceholder}
        sx={{
          ...props.avatarPlaceholder.sx,
          color: "lightestText",
        }}
      />
    );
  }
  if (profile.profileImage?.publicId) {
    const { publicId, cloudName } = profile.profileImage;
    return (
      <Avatar
        src={`https://res.cloudinary.com/${cloudName}/image/upload/w_${props.avatar.width},h_${props.avatar.height},c_fill,g_face,dpr_3/${publicId}`}
        {...props.avatar}
      />
    );
  }
  return <Avatar src={url} {...props.avatar} />;
}

type SkeletonProps = Omit<
  ComponentPropsWithoutRef<typeof Flex>,
  "children" | "sx"
> & {
  variant?: keyof typeof avatarVariants;
};
export function UserProfileCardAvatarSkeleton({
  variant = "default",
  ...rest
}: SkeletonProps) {
  const props = avatarVariants[variant];
  return (
    <Flex
      gap={0}
      {...props.root}
      sx={(theme: Theme) => ({
        ...props.root.sx(theme),
        margin: "auto",
        backgroundColor: "nuanced",
        transition: "transform",
        justifyContent: "center",
        alignItems: "center",
      })}
      {...rest}
    >
      <Skeleton
        {...props.avatar}
        sx={{
          ...props.avatar.sx,
          color: "lightestText",
        }}
      />
    </Flex>
  );
}

function AvatarPlaceholder({
  data,
  ...rest
}: {
  data: FragmentType<typeof fragment>;
}) {
  const profile = useFragment(fragment, data);
  const nameTokens = profile.displayName?.toUpperCase().split(/\s+/) || [];
  const placeholder =
    nameTokens.length === 0
      ? ""
      : nameTokens.length === 1
      ? nameTokens[0]?.[0]
      : `${nameTokens[0]?.[0]}${nameTokens[nameTokens.length - 1]?.[0]}`;
  return (
    <Title
      variant="small"
      sx={{ fontWeight: "bold" }}
      {...rest}
    >
      {placeholder}
    </Title>
  );
}

const avatarVariants = {
  default: {
    root: {
      sx: (theme: Theme) => ({
        width: 100,
        height: 100,
        borderRadius: "round",
        border: "1px solid white",
        boxShadow: `0 0 0 4px ${theme.colors?.nuanced}`,
      }),
    },
    avatar: { width: 100, height: 100, sx: { borderRadius: "round" } },
    avatarPlaceholder: { sx: {} },
  },
  small: {
    root: {
      sx: (theme: Theme) => ({
        width: 34,
        height: 34,
        borderRadius: "round",
        border: "1px solid white",
        boxShadow: `0 0 0 1px ${theme.colors?.nuanced}`,
      }),
    },
    avatar: { width: 32, height: 32, sx: { borderRadius: "round" } },
    avatarPlaceholder: { sx: { fontSize: "small" } },
  },
  preview: {
    root: {
      sx: (theme: Theme) => ({
        width: 60,
        height: 60,
        borderRadius: "large",
        border: "1px solid white",
        boxShadow: `0 0 0 1px ${theme.colors?.nuanced}`,
      }),
    },
    avatar: { width: 58, height: 58, sx: { borderRadius: "default" } },
    avatarPlaceholder: { sx: { fontSize: "small" } },
  },
  compact: {
    root: {
      sx: (theme: Theme) => ({
        width: 34,
        height: 34,
        borderRadius: "large",
        border: "1px solid white",
        boxShadow: `0 0 0 1px ${theme.colors?.nuanced}`,
      }),
    },
    avatar: { width: 32, height: 32, sx: { borderRadius: "default" } },
    avatarPlaceholder: { sx: { fontSize: "small" } },
  },
} as const;
